import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, Check } from 'lucide-react';
import { API } from '@/App';
import StripeInAppCheckout from './StripeInAppCheckout';
import { isMobileBuild } from '@/lib/mobileBuild';
import UpgradeOnWebNotice from './UpgradeOnWebNotice';

const DripRemovalModal = ({ isOpen, onClose, onSuccess, currentTier }) => {
  const [price, setPrice] = useState(2605); // $25.00 base + 3% + $0.30
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPrice();
      setShowCheckout(false);
    }
  }, [isOpen]);

  const fetchPrice = async () => {
    try {
      const response = await axios.get(`${API}/drip-removal-price`, { params: { tier: currentTier } });
      setPrice(response.data.charge_cents);
    } catch (error) {
      console.error('Failed to fetch pricing', error);
    }
  };

  const formatPrice = (cents) => {
    return (cents / 100).toFixed(2);
  };

  const handlePaymentSuccess = () => {
    toast.success('Drip removal activated! All modules are now unlocked.');
    onSuccess?.();
    onClose();
  };

  if (isMobileBuild()) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <Zap className="w-6 h-6 text-yellow-500" />
              Remove Drip Feed
            </DialogTitle>
          </DialogHeader>
          <UpgradeOnWebNotice
            title="Unlock all folders on our website"
            description="The drip-removal upgrade is available on our website. Purchase there once and all your content unlocks here automatically."
            ctaLabel="Upgrade on Website"
            path="/dashboard/modules"
            testId="drip-removal-web-notice"
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Zap className="w-6 h-6 text-yellow-500" />
            Remove Drip Feed
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Feature Explanation */}
          <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
            <CardHeader>
              <CardTitle className="text-lg">Unlock All Modules Instantly</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 mb-4">
                By default, modules unlock as you complete previous ones with time delays. 
                This add-on removes all restrictions and gives you immediate access to all modules in your tier.
              </p>
              
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Instant access to all tier modules</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">No waiting periods between modules</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">One-time payment (permanent)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Learn at your own pace</span>
                </li>
              </ul>

              {!showCheckout && (
                <div className="bg-white rounded-lg p-4 border border-yellow-300">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600">One-Time Price:</span>
                    <span className="text-3xl font-bold text-orange-600">
                      ${formatPrice(price)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 text-right">includes 3% + $0.30 processing fee</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* In-App Checkout or CTA */}
          {showCheckout ? (
            <StripeInAppCheckout
              itemType="tier_upgrade"
              tier={currentTier}
              paymentOption="yearly"
              isAddon={true}
              onSuccess={handlePaymentSuccess}
              onError={(msg) => toast.error(msg)}
            />
          ) : (
            <Button
              onClick={() => setShowCheckout(true)}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="drip-removal-checkout-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              Continue to Payment
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DripRemovalModal;
